<template>
  <div class="campaign-detail-page">
    <CommonOverlay :show="busy" />

    <!-- Back Button -->
    <div class="back-nav mb-3">
      <b-button variant="link" class="back-btn p-0" @click="goBack">
        <i class="fa fa-arrow-left mr-2"></i> Back to Campaigns
      </b-button>
    </div>

    <!-- Header Card -->
    <b-card class="campaign-header-card mb-4">
      <div class="header-content">
        <div class="header-left">
          <div class="title-row">
            <h2 class="campaign-title">{{ campaign.Name }}</h2>
            <b-badge :variant="getStatusVariant" class="status-badge ml-2">
              <i :class="getStatusIcon" class="mr-1"></i>
              {{ campaign.Status }}
            </b-badge>
          </div>
          
          <p class="campaign-description" v-if="campaign.Description">
            {{ campaign.Description }}
          </p>
          
          <div class="campaign-meta">
            <div class="meta-item">
              <div class="meta-icon">
                <i :class="getTypeIcon"></i>
              </div>
              <span class="meta-text">{{ getTypeLabel }}</span>
            </div>
            <div class="meta-divider"></div>
            <div class="meta-item">
              <i class="fa fa-calendar-o"></i>
              <span>Created {{ formatDateTimeWitTz(campaign.CreatedAt, siteTimezone) }}</span>
            </div>
            <div class="meta-divider" v-if="campaign.SentAt"></div>
            <div class="meta-item" v-if="campaign.SentAt">
              <i class="fa fa-paper-plane"></i>
              <span>Sent {{ formatDateTimeWitTz(campaign.SentAt, siteTimezone) }}</span>
            </div>
            <div class="meta-divider"></div>
            <div class="meta-item">
              <i class="fa fa-user"></i>
              <span>{{ campaign.CreatedBy }}</span>
            </div>
          </div>
        </div>
        
      </div>
    </b-card>

    <ErrorDisplay />

    <!-- Sending Progress Section (shown while campaign is actively sending) -->
    <b-card class="sending-progress-card mb-4" v-if="isSending">
      <div class="d-flex align-items-center mb-3">
        <b-spinner small variant="primary" class="mr-2"></b-spinner>
        <h5 class="mb-0">Campaign is sending...</h5>
      </div>
      <b-progress :max="campaign.TotalRecipients || 1" height="1.5rem" class="mb-3">
        <b-progress-bar :value="campaign.SentCount || 0" variant="success">
          {{ campaign.SentCount || 0 }} sent
        </b-progress-bar>
        <b-progress-bar :value="campaign.FailedCount || 0" variant="danger">
          {{ (campaign.FailedCount || 0) > 0 ? (campaign.FailedCount + ' failed') : '' }}
        </b-progress-bar>
      </b-progress>
      <div class="d-flex justify-content-between text-muted">
        <span>{{ progressPercent }}% complete</span>
        <span>{{ (campaign.SentCount || 0) + (campaign.FailedCount || 0) }} / {{ campaign.TotalRecipients || 0 }} recipients</span>
      </div>
    </b-card>

    <!-- Retry Failed Section (shown when campaign is complete with failures) -->
    <b-alert variant="warning" show v-if="hasFailedRecipients && !isSending" class="mb-4">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <i class="fa fa-exclamation-triangle mr-2"></i>
          <strong>{{ campaign.FailedCount }} recipient{{ campaign.FailedCount > 1 ? 's' : '' }} failed.</strong>
          You can retry sending to failed recipients.
        </div>
        <b-button variant="warning" size="sm" @click="retryFailed" :disabled="busy">
          <i class="fa fa-refresh mr-1"></i> Retry Failed
        </b-button>
      </div>
    </b-alert>

    <!-- AI Call Results (replaces email/SMS metrics + analytics for ai_call campaigns) -->
    <template v-if="isAiCall">
      <!-- Call Summary Metrics -->
      <div class="metrics-grid mb-4">
        <div class="metric-card">
          <div class="metric-icon-wrapper recipients">
            <i class="fa fa-list-ol"></i>
          </div>
          <div class="metric-info">
            <span class="metric-value">{{ (aiCallSummary.queued || 0) | formatNumber }}</span>
            <span class="metric-label">Queued</span>
          </div>
        </div>

        <div class="metric-card">
          <div class="metric-icon-wrapper sent">
            <i class="fa fa-phone"></i>
          </div>
          <div class="metric-info">
            <span class="metric-value">{{ (aiCallSummary.reached || 0) | formatNumber }}</span>
            <span class="metric-label">Reached</span>
          </div>
        </div>

        <div class="metric-card">
          <div class="metric-icon-wrapper delivered">
            <i class="fa fa-calendar-check-o"></i>
          </div>
          <div class="metric-info">
            <span class="metric-value">{{ (aiCallSummary.booked || 0) | formatNumber }}</span>
            <span class="metric-label">Booked</span>
          </div>
        </div>

        <div class="metric-card">
          <div class="metric-icon-wrapper" style="background: rgba(245, 158, 11, 0.1); color: #f59e0b;">
            <i class="fa fa-step-forward"></i>
          </div>
          <div class="metric-info">
            <span class="metric-value">{{ (aiCallSummary.skipped || 0) | formatNumber }}</span>
            <span class="metric-label">Skipped</span>
          </div>
        </div>

        <div class="metric-card">
          <div class="metric-icon-wrapper clicked">
            <i class="fa fa-phone-volume"></i>
          </div>
          <div class="metric-info">
            <span class="metric-value">{{ (aiCallSummary.totalCalls || 0) | formatNumber }}</span>
            <span class="metric-label">Total Calls</span>
          </div>
        </div>
      </div>

      <!-- Outcome + Recipient breakdowns -->
      <b-row>
        <b-col lg="6" class="mb-4">
          <b-card class="analytics-card h-100">
            <h5 class="section-title mb-3">
              <i class="fa fa-pie-chart mr-2"></i>Call Outcomes
            </h5>
            <div v-if="aiCallByOutcome.length" class="stats-breakdown">
              <div class="stat-row" v-for="row in aiCallByOutcome" :key="'outcome-' + row.key">
                <span class="stat-label">{{ row.key | humanizeKey }}</span>
                <span class="stat-value">{{ row.count | formatNumber }}</span>
              </div>
            </div>
            <div v-else class="text-center py-4 text-muted">
              <p class="mb-0">No call outcomes recorded yet</p>
            </div>
          </b-card>
        </b-col>
        <b-col lg="6" class="mb-4">
          <b-card class="analytics-card h-100">
            <h5 class="section-title mb-3">
              <i class="fa fa-users mr-2"></i>Recipients
            </h5>
            <div v-if="aiCallRecipients.length" class="stats-breakdown">
              <div class="stat-row" v-for="row in aiCallRecipients" :key="'recip-' + row.key">
                <span class="stat-label">{{ row.key | humanizeKey }}</span>
                <span class="stat-value">{{ row.count | formatNumber }}</span>
              </div>
            </div>
            <div v-else class="text-center py-4 text-muted">
              <p class="mb-0">No recipient status recorded yet</p>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </template>

    <!-- Key Metrics Cards (email/SMS campaigns only) -->
    <div v-if="!isAiCall" class="metrics-grid mb-4">
      <div class="metric-card">
        <div class="metric-icon-wrapper recipients">
          <i class="fa fa-users"></i>
        </div>
        <div class="metric-info">
          <span class="metric-value">{{ campaign.Type === 'Both' ? (campaign.UniquePatientCount || 0) : (campaign.TotalRecipients || campaign.RecipientCount || 0) | formatNumber }}</span>
          <span class="metric-label">{{ campaign.Type === 'Both' ? 'Patients' : 'Total Recipients' }}</span>
        </div>
      </div>
      
      <div class="metric-card">
        <div class="metric-icon-wrapper sent">
          <i class="fa fa-paper-plane"></i>
        </div>
        <div class="metric-info">
          <span class="metric-value">{{ campaign.SentCount | formatNumber }}</span>
          <span class="metric-label">Messages Sent</span>
        </div>
      </div>
      
      <div class="metric-card">
        <div class="metric-icon-wrapper delivered">
          <i class="fa fa-check-circle"></i>
        </div>
        <div class="metric-info">
          <span class="metric-value">{{ campaign.DeliveredCount | formatNumber }}</span>
          <span class="metric-label">Delivered</span>
          <span class="metric-rate success">{{ deliveryRate }}%</span>
        </div>
      </div>
      
      <div class="metric-card" v-if="campaign.Type === 'Email' || campaign.Type === 'Both'">
        <div class="metric-icon-wrapper opened">
          <i class="fa fa-envelope-open"></i>
        </div>
        <div class="metric-info">
          <span class="metric-value">{{ campaign.OpenCount | formatNumber }}</span>
          <span class="metric-label">Opened</span>
          <span class="metric-rate info">{{ openRate }}%</span>
        </div>
      </div>
      
      <div class="metric-card">
        <div class="metric-icon-wrapper clicked">
          <i class="fa fa-mouse-pointer"></i>
        </div>
        <div class="metric-info">
          <span class="metric-value">{{ clickRate }}%</span>
          <span class="metric-label">Click Rate</span>
        </div>
      </div>

      <div class="metric-card">
        <div class="metric-icon-wrapper" style="background: rgba(139, 92, 246, 0.1); color: #8b5cf6;">
          <i class="fa fa-hand-pointer-o"></i>
        </div>
        <div class="metric-info">
          <span class="metric-value">{{ (campaign.TotalClicks || 0) | formatNumber }}</span>
          <span class="metric-label">Total Clicks</span>
        </div>
      </div>

      <div class="metric-card">
        <div class="metric-icon-wrapper" style="background: rgba(14, 165, 233, 0.1); color: #0ea5e9;">
          <i class="fa fa-user-plus"></i>
        </div>
        <div class="metric-info">
          <span class="metric-value">{{ (campaign.ClickedCount || 0) | formatNumber }}</span>
          <span class="metric-label">Unique Clickers</span>
        </div>
      </div>

      <div class="metric-card" v-if="campaign.FailedCount > 0">
        <div class="metric-icon-wrapper" style="background: rgba(220, 53, 69, 0.1); color: #dc3545;">
          <i class="fa fa-times-circle"></i>
        </div>
        <div class="metric-info">
          <span class="metric-value">{{ campaign.FailedCount | formatNumber }}</span>
          <span class="metric-label">Failed</span>
        </div>
      </div>

    </div>

    <!-- Analytics Tabs (email/SMS campaigns only — AI Call uses the Call Results panel above) -->
    <b-card v-if="!isAiCall" class="analytics-card">
      <b-tabs content-class="mt-4" nav-class="analytics-tabs" fill>
        <!-- Recipients Tab (Overview) -->
        <b-tab title="Recipients" active>
          <div class="recipients-section">
            <div class="recipients-toolbar">
              <div class="search-input-wrapper">
                <i class="fa fa-search search-icon"></i>
                <b-form-input
                  v-model="recipientSearch"
                  placeholder="Search by name or phone..."
                  class="search-input"
                />
              </div>
              <b-form-select v-model="recipientFilter" class="filter-select">
                <b-form-select-option value="all">All Recipients</b-form-select-option>
                <b-form-select-option value="sent">Sent</b-form-select-option>
                <b-form-select-option value="delivered">Delivered</b-form-select-option>
                <b-form-select-option value="clicked">Clicked</b-form-select-option>
                <b-form-select-option value="not_clicked">Not Clicked</b-form-select-option>
                <b-form-select-option value="failed">Failed</b-form-select-option>
                <b-form-select-option value="skipped">Skipped</b-form-select-option>
                <b-form-select-option value="pending">Pending</b-form-select-option>
              </b-form-select>
            </div>

            <div class="table-responsive">
              <b-table
                :items="paginatedRecipients"
                :fields="recipientFields"
                hover
                class="recipients-table mb-0"
                show-empty
              >
                <template #cell(Name)="data">
                  <strong>{{ data.item.Name }}</strong>
                </template>
                <template #cell(Phone)="data">
                  {{ data.item.Phone || '—' }}
                </template>
                <template #cell(Email)="data">
                  {{ data.item.Email || '—' }}
                </template>
                <template #cell(Status)="data">
                  <template v-if="campaign.Type === 'Both'">
                    <div class="channel-status">
                      <div v-if="data.item.SmsStatus" class="mb-1">
                        <span class="channel-badge channel-sms">SMS</span>
                        <b-badge :variant="getRecipientStatusVariant(data.item.SmsStatus)" pill>{{ data.item.SmsStatus }}</b-badge>
                      </div>
                      <div v-if="data.item.EmailStatus">
                        <span class="channel-badge channel-email">Email</span>
                        <b-badge :variant="getRecipientStatusVariant(data.item.EmailStatus)" pill>{{ data.item.EmailStatus }}</b-badge>
                      </div>
                    </div>
                  </template>
                  <template v-else>
                    <b-badge :variant="getRecipientStatusVariant(data.item.Status)" pill>
                      {{ data.item.Status }}
                    </b-badge>
                    <i v-if="data.item.ClickCount > 0" class="fa fa-mouse-pointer ml-1 text-primary" title="Clicked"></i>
                    <!-- Why this recipient wasn't reached. Backend writes it on every
                         skipped/failed row; without this the practice sees the outcome
                         but never the reason. -->
                    <div
                      v-if="recipientStatusReason(data.item)"
                      class="recipient-status-reason"
                      :title="recipientStatusReason(data.item)"
                    >
                      {{ recipientStatusReason(data.item) }}
                    </div>
                  </template>
                </template>
                <template #cell(Clicked)="data">
                  <template v-if="campaign.Type === 'Both'">
                    <div class="channel-clicks">
                      <div v-if="data.item.SmsClickedAt" class="mb-1">
                        <span class="channel-badge channel-sms">SMS</span>
                        {{ formatDateTimeWitTz(data.item.SmsClickedAt, siteTimezone) }}
                      </div>
                      <div v-if="data.item.EmailClickedAt">
                        <span class="channel-badge channel-email">Email</span>
                        {{ formatDateTimeWitTz(data.item.EmailClickedAt, siteTimezone) }}
                      </div>
                      <span v-if="!data.item.SmsClickedAt && !data.item.EmailClickedAt" class="text-muted">—</span>
                    </div>
                  </template>
                  <template v-else>
                    <span v-if="data.item.ClickedAt">{{ formatDateTimeWitTz(data.item.ClickedAt, siteTimezone) }}</span>
                    <span v-else class="text-muted">—</span>
                  </template>
                </template>
                <template #cell(actions)="data">
                  <b-button
                    size="sm"
                    variant="outline-primary"
                    @click="viewPatient(data.item)"
                    class="view-btn"
                  >
                    <i class="fa fa-user"></i>
                  </b-button>
                </template>
                <template #empty>
                  <div class="text-center py-4 text-muted">
                    <i class="fa fa-inbox fa-2x mb-2 d-block"></i>
                    <p class="mb-0">No recipients found</p>
                  </div>
                </template>
              </b-table>
            </div>

            <div class="recipients-footer">
              <span class="results-count">
                Showing {{ Math.min(filteredRecipients.length, recipientPerPage) }} of {{ filteredRecipients.length }} recipients
              </span>
              <b-pagination
                v-if="filteredRecipients.length > recipientPerPage"
                v-model="recipientPage"
                :total-rows="filteredRecipients.length"
                :per-page="recipientPerPage"
                size="sm"
                class="mb-0"
              />
            </div>
          </div>
        </b-tab>

        <!-- Message Content Tab -->
        <b-tab title="Message Content">
          <div class="message-content-section">
            <!-- SMS Content -->
            <div v-if="campaign.Type === 'SMS' || campaign.Type === 'Both'" class="message-block mb-4">
              <h5 class="section-title">
                <i class="fa fa-comment text-success mr-2"></i>SMS Message
              </h5>
              <b-card class="message-preview-card" v-if="smsContent">
                <div class="sms-preview">
                  <div class="sms-bubble">
                    {{ smsContent }}
                  </div>
                </div>
                <div class="message-stats mt-3">
                  <span class="stat">
                    <i class="fa fa-text-width mr-1"></i>
                    {{ smsContent.length }} characters
                  </span>
                  <span class="stat" v-if="smsContent.length > 160">
                    <i class="fa fa-clone mr-1"></i>
                    {{ Math.ceil(smsContent.length / 153) }} segments
                  </span>
                </div>
              </b-card>
              <div v-else class="text-center py-4 text-muted">
                <p class="mb-0">No SMS content available</p>
              </div>
            </div>

            <!-- Email Content -->
            <div v-if="campaign.Type === 'Email' || campaign.Type === 'Both'" class="message-block">
              <h5 class="section-title">
                <i class="fa fa-envelope text-primary mr-2"></i>Email Message
              </h5>
              <b-card class="message-preview-card" v-if="campaign.Subject || campaign.HTMLContent">
                <div class="email-preview">
                  <div class="email-subject" v-if="campaign.Subject">
                    <label>Subject:</label>
                    <span>{{ campaign.Subject }}</span>
                  </div>
                  <div class="email-body" v-if="campaign.HTMLContent" v-html="campaign.HTMLContent"></div>
                  <div class="email-body" v-else-if="campaign.TextContent">
                    {{ campaign.TextContent }}
                  </div>
                </div>
              </b-card>
              <div v-else class="text-center py-4 text-muted">
                <p class="mb-0">No email content available</p>
              </div>
            </div>
          </div>
        </b-tab>

        <!-- Activity Log Tab -->
        <b-tab title="Activity Log">
          <div class="activity-log-section">
            <div class="activity-timeline" v-if="computedActivityLog.length">
              <div
                v-for="(activity, index) in computedActivityLog"
                :key="index"
                class="activity-item"
              >
                <div class="activity-icon" :class="activity.type">
                  <i :class="activity.icon"></i>
                </div>
                <div class="activity-content">
                  <p class="activity-message">{{ activity.message }}</p>
                  <span class="activity-time">{{ formatDateTimeWitTz(activity.timestamp, siteTimezone) }}</span>
                </div>
              </div>
            </div>
            <div v-else class="text-center py-4 text-muted">
              <p class="mb-0">No activity recorded yet</p>
            </div>
          </div>
        </b-tab>

        <!-- Opens & Clicks Tab -->
        <b-tab title="Opens & Clicks" @click="loadClickData">
          <div class="opens-clicks-section">
            <b-row>
              <b-col lg="6" class="mb-4">
                <div class="chart-section">
                  <h5 class="section-title">
                    <i class="fa fa-envelope-open-o mr-2"></i>Opens Breakdown
                    <small v-if="campaign.Type === 'Both'" class="text-muted ml-1">(Email only)</small>
                    <small v-if="campaign.Type === 'SMS'" class="text-muted ml-1">(Not available for SMS)</small>
                  </h5>
                  <div class="stats-breakdown">
                    <div class="stat-row">
                      <span class="stat-label">Total Opens</span>
                      <span class="stat-value">{{ campaign.OpenCount | formatNumber }}</span>
                    </div>
                    <div class="stat-row">
                      <span class="stat-label">Unique Opens</span>
                      <span class="stat-value">{{ campaign.UniqueOpenCount | formatNumber }}</span>
                    </div>
                    <div class="stat-row">
                      <span class="stat-label">Open Rate</span>
                      <span class="stat-value text-success">{{ openRate }}%</span>
                    </div>
                  </div>
                </div>
              </b-col>
              <b-col lg="6" class="mb-4">
                <div class="chart-section">
                  <h5 class="section-title">
                    <i class="fa fa-mouse-pointer mr-2"></i>Clicks Breakdown
                  </h5>
                  <div class="stats-breakdown">
                    <div class="stat-row">
                      <span class="stat-label">Total Clicks</span>
                      <span class="stat-value">{{ campaign.ClickCount | formatNumber }}</span>
                    </div>
                    <div class="stat-row">
                      <span class="stat-label">Unique Clicks</span>
                      <span class="stat-value">{{ campaign.UniqueClickCount | formatNumber }}</span>
                    </div>
                    <div class="stat-row">
                      <span class="stat-label">Click Rate</span>
                      <span class="stat-value text-primary">{{ clickRate }}%</span>
                    </div>
                    <div class="stat-row">
                      <span class="stat-label">CTOR</span>
                      <span class="stat-value text-info">{{ ctorRate }}%</span>
                    </div>
                  </div>
                </div>
              </b-col>
            </b-row>

            <!-- Link Performance -->
            <div class="chart-section mt-4">
              <h5 class="section-title">
                <i class="fa fa-link mr-2"></i>Link Performance
              </h5>
              <div v-if="clicksLoading" class="text-center py-3">
                <b-spinner small></b-spinner> Loading click data...
              </div>
              <div v-else-if="!clickData.linkSummary || clickData.linkSummary.length === 0" class="text-center py-3 text-muted">
                <p class="mb-0">No click data available</p>
              </div>
              <div v-else class="table-responsive">
                <table class="table table-sm">
                  <thead>
                    <tr style="background: #f8fafc;">
                      <th>Link URL</th>
                      <th class="text-center" v-if="campaign.Type === 'Both'">Channel</th>
                      <th class="text-center">Total Clicks</th>
                      <th class="text-center">Unique Clickers</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(link, idx) in clickData.linkSummary" :key="idx">
                      <td>
                        <a :href="link.OriginalUrl" target="_blank" class="text-primary" style="word-break: break-all;">
                          {{ link.OriginalUrl.length > 60 ? link.OriginalUrl.substring(0, 60) + '...' : link.OriginalUrl }}
                        </a>
                      </td>
                      <td class="text-center" v-if="campaign.Type === 'Both'">
                        <span v-if="link.SmsClicks > 0" class="channel-badge channel-sms mr-1">SMS: {{ link.SmsClicks }}</span>
                        <span v-if="link.EmailClicks > 0" class="channel-badge channel-email">Email: {{ link.EmailClicks }}</span>
                      </td>
                      <td class="text-center font-weight-bold">{{ link.TotalClicks }}</td>
                      <td class="text-center font-weight-bold">{{ link.UniqueClickers }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Click Timeline -->
            <div class="chart-section mt-4" v-if="clickData.clickEvents && clickData.clickEvents.length > 0">
              <h5 class="section-title">
                <i class="fa fa-clock-o mr-2"></i>Click Timeline
              </h5>
              <div class="click-timeline">
                <div v-for="(evt, idx) in clickData.clickEvents" :key="idx" class="click-event-item">
                  <div class="click-event-icon">
                    <i class="fa fa-mouse-pointer"></i>
                  </div>
                  <div class="click-event-content">
                    <p class="mb-0">
                      <strong>{{ [evt.FName, evt.LName].filter(Boolean).join(' ') || 'Unknown' }}</strong>
                      <span v-if="campaign.Type === 'Both'" :class="['channel-badge', evt.EventType === 'sms_click' ? 'channel-sms' : 'channel-email']">
                        {{ evt.EventType === 'sms_click' ? 'SMS' : 'Email' }}
                      </span>
                      clicked
                      <a :href="evt.URL" target="_blank" class="text-primary">
                        {{ evt.URL.length > 40 ? evt.URL.substring(0, 40) + '...' : evt.URL }}
                      </a>
                    </p>
                    <small class="text-muted">{{ formatDateTimeWitTz(evt.EventTime, siteTimezone) }}</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </b-tab>

      </b-tabs>
    </b-card>

  </div>
</template>

<script>
/* eslint-disable */
import { mapState } from 'vuex';
import ErrorDisplay from '@/components/ErrorDisplay/ErrorDisplay';
import CommonOverlay from '@/components/CommonOverlay';
import CampaignDonutChart from './components/CampaignDonutChart.vue';
import CampaignTimelineChart from './components/CampaignTimelineChart.vue';
import mixin from '../../mixin.js';

export default {
  name: 'CampaignDetail',
  components: {
    ErrorDisplay,
    CommonOverlay,
    CampaignDonutChart,
    CampaignTimelineChart
  },
  mixins: [mixin],

  data() {
    return {
      busy: false,
      recipientSearch: '',
      recipientFilter: 'all',
      recipientPage: 1,
      recipientPerPage: 10,

      // Click tracking
      clicksLoading: false,
      clicksLoaded: false,
      clickData: { linkSummary: [], clickEvents: [] },

      // Campaign data loaded from API
      campaign: {},

      // Click data loaded via fetchCampaignClicks (linkSummary + clickEvents)
      // Used by both Clicks tab (SMS) and Opens & Clicks tab (Email)

      recipients: [],

      recipientFields: [
        { key: 'Name', label: 'Name', tdClass: 'align-middle' },
        { key: 'Phone', label: 'Cell', tdClass: 'align-middle' },
        { key: 'Email', label: 'Email', tdClass: 'align-middle' },
        { key: 'Status', label: 'Status', tdClass: 'align-middle' },
        { key: 'Clicked', label: 'Clicked', tdClass: 'align-middle' },
        { key: 'actions', label: '', class: 'text-center', tdClass: 'align-middle' }
      ],

      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: false
        }
      },

      timelineOptions: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          xAxes: [{
            type: 'time',
            time: {
              unit: 'hour'
            }
          }],
          yAxes: [{
            beginAtZero: true
          }]
        }
      }
    };
  },

  computed: {
    ...mapState('customer', ['siteID', 'settings']),

    // AI Call campaigns get a dedicated "Call Results" view (no email/SMS message stats).
    // Detect via Type === 'ai_call' (case-insensitive) or the presence of the aiCall payload.
    isAiCall() {
      const type = (this.campaign.Type || '').toLowerCase();
      return type === 'ai_call' || !!this.campaign.aiCall;
    },

    // The aiCall object returned by GET /campaigns/{id} for ai_call campaigns:
    // { channel, summary:{...}, byOutcome:{...}, recipients:{...} }
    aiCall() {
      return this.campaign.aiCall || {};
    },

    aiCallSummary() {
      return this.aiCall.summary || {};
    },

    // byOutcome / recipients as [{ key, count }] for simple breakdown lists
    aiCallByOutcome() {
      const obj = this.aiCall.byOutcome || {};
      return Object.keys(obj).map(key => ({ key, count: obj[key] }));
    },

    aiCallRecipients() {
      const obj = this.aiCall.recipients || {};
      return Object.keys(obj).map(key => ({ key, count: obj[key] }));
    },

    isSending() {
      return this.campaign.Status === 'sending';
    },

    hasFailedRecipients() {
      return (this.campaign.FailedCount || 0) > 0;
    },

    progressPercent() {
      const total = this.campaign.TotalRecipients || 0;
      if (total === 0) return 0;
      const processed = (this.campaign.SentCount || 0) + (this.campaign.FailedCount || 0);
      return Math.round((processed / total) * 100);
    },

    dateFormatStr() {
      return this.settings?.GENERAL_DATE_FORMAT?.PropertyValue || 'MM/DD/YYYY';
    },

    siteTimezone() {
      return this.allSettings?.GENERAL_TIMEZONE?.PropertyValue || 'America/New_York';
    },

    getStatusVariant() {
      const variants = {
        'draft': 'secondary',
        'scheduled': 'warning',
        'sending': 'primary',
        'sent': 'success',
        'failed': 'danger',
        'paused': 'dark'
      };
      return variants[this.campaign.Status] || 'secondary';
    },

    getStatusIcon() {
      const icons = {
        'draft': 'fa fa-edit',
        'scheduled': 'fa fa-clock-o',
        'sending': 'fa fa-spinner fa-spin',
        'sent': 'fa fa-check',
        'failed': 'fa fa-times',
        'paused': 'fa fa-pause'
      };
      return icons[this.campaign.Status] || 'fa fa-circle';
    },

    getTypeIcon() {
      if (this.isAiCall) return 'fa fa-phone-volume';
      const icons = {
        'SMS': 'fa fa-comment',
        'Email': 'fa fa-envelope',
        'Both': 'fa fa-comments'
      };
      return icons[this.campaign.Type] || 'fa fa-paper-plane';
    },

    getTypeLabel() {
      if (this.isAiCall) return 'AI Call Campaign';
      const labels = {
        'SMS': 'SMS Campaign',
        'Email': 'Email Campaign',
        'Both': 'SMS + Email Campaign'
      };
      return labels[this.campaign.Type] || this.campaign.Type;
    },

    deliveryRate() {
      const sent = this.campaign.SentCount || 0;
      const delivered = this.campaign.DeliveredCount || 0;
      if (!sent) return '0.0';
      return ((delivered / sent) * 100).toFixed(1);
    },

    openRate() {
      const delivered = this.campaign.DeliveredCount || 0;
      const opened = this.campaign.OpenCount || 0;
      if (!delivered) return '0.0';
      return ((opened / delivered) * 100).toFixed(1);
    },

    clickRate() {
      const clicked = this.campaign.ClickedCount || 0;
      // Prefer delivered as the denominator, but fall back to sent (then total recipients) when
      // DeliveredCount is 0 — e.g. SMS delivery receipts haven't landed yet, or a carrier never
      // sends one — so real clicks still surface a rate instead of a misleading 0.0%.
      const denom = this.campaign.DeliveredCount || this.campaign.SentCount || this.campaign.TotalRecipients || 0;
      if (!denom) return '0.0';
      return ((clicked / denom) * 100).toFixed(1);
    },


    ctorRate() {
      const opened = this.campaign.UniqueOpenCount || 0;
      const clicked = this.campaign.UniqueClickCount || 0;
      if (!opened) return '0.0';
      return ((clicked / opened) * 100).toFixed(1);
    },


    pendingCount() {
      return Math.max(0, this.campaign.SentCount - this.campaign.DeliveredCount - this.campaign.BounceCount);
    },

    sentPercentage() {
      if (!this.campaign.RecipientCount) return 0;
      return (this.campaign.SentCount / this.campaign.RecipientCount) * 100;
    },

    deliveredPercentage() {
      if (!this.campaign.RecipientCount) return 0;
      return (this.campaign.DeliveredCount / this.campaign.RecipientCount) * 100;
    },

    openedPercentage() {
      if (!this.campaign.RecipientCount) return 0;
      return (this.campaign.OpenCount / this.campaign.RecipientCount) * 100;
    },

    clickedPercentage() {
      if (!this.campaign.RecipientCount) return 0;
      return (this.campaign.ClickCount / this.campaign.RecipientCount) * 100;
    },

    deliveryChartData() {
      return {
        labels: ['Delivered', 'Bounced', 'Pending'],
        datasets: [{
          data: [this.campaign.DeliveredCount, this.campaign.BounceCount, this.pendingCount],
          backgroundColor: ['#3abf94', '#ef4444', '#f59e0b'],
          borderWidth: 0
        }]
      };
    },

    timelineChartData() {
      return {
        labels: ['9:00', '9:30', '10:00', '10:30', '11:00', '11:30', '12:00'],
        datasets: [{
          label: 'Messages Sent',
          data: [450, 820, 1200, 1800, 2100, 2350, 2450],
          borderColor: '#3abf94',
          backgroundColor: 'rgba(58, 191, 148, 0.1)',
          fill: true
        }]
      };
    },

    opensClicksTimelineData() {
      return {
        labels: ['9:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00'],
        datasets: [
          {
            label: 'Opens',
            data: [120, 380, 620, 890, 1050, 1280, 1420, 1520],
            borderColor: '#06b6d4',
            backgroundColor: 'rgba(6, 182, 212, 0.1)',
            fill: true
          },
          {
            label: 'Clicks',
            data: [25, 85, 145, 220, 295, 365, 425, 486],
            borderColor: '#3b82f6',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            fill: true
          }
        ]
      };
    },

    filteredRecipients() {
      let filtered = [...this.recipients];

      if (this.recipientSearch) {
        const query = this.recipientSearch.toLowerCase();
        filtered = filtered.filter(r =>
          (r.Name || '').toLowerCase().includes(query) ||
          (r.Phone || '').includes(query) ||
          (r.Email || '').toLowerCase().includes(query)
        );
      }

      if (this.recipientFilter === 'clicked') {
        filtered = filtered.filter(r => (r.ClickCount || 0) > 0);
      } else if (this.recipientFilter === 'not_clicked') {
        filtered = filtered.filter(r => (r.ClickCount || 0) === 0 && (r.Status || '').toLowerCase() === 'delivered');
      } else if (this.recipientFilter !== 'all') {
        filtered = filtered.filter(r => (r.Status || '').toLowerCase() === this.recipientFilter);
      }

      return filtered;
    },

    paginatedRecipients() {
      const start = (this.recipientPage - 1) * this.recipientPerPage;
      return this.filteredRecipients.slice(start, start + this.recipientPerPage);
    },

    smsContent() {
      const raw = this.campaign.TextContent || this.campaign.SmsContent || '';
      // Clean up HTML anchor tags for readability — show [Link: text] instead of raw HTML
      return raw.replace(/<a\s+[^>]*>(.*?)<\/a>/gi, (_match, displayText) => {
        const cleanText = displayText.replace(/<[^>]*>/g, '').trim();
        return cleanText ? `[Link: ${cleanText}]` : '';
      });
    },

    computedActivityLog() {
      const events = [];
      const c = this.campaign;

      if (c.CreatedAt) {
        events.push({ type: 'default', icon: 'fa fa-plus', message: `Campaign created${c.CreatedBy ? ' by ' + c.CreatedBy : ''}`, timestamp: c.CreatedAt });
      }
      if (c.ScheduledAt && (c.Status === 'scheduled' || c.Status === 'sent' || c.Status === 'sending')) {
        events.push({ type: 'warning', icon: 'fa fa-clock-o', message: `Campaign scheduled for ${this.formatDateTimeWitTz(c.ScheduledAt, this.siteTimezone)}`, timestamp: c.ScheduledAt });
      }
      if (c.StartedAt) {
        events.push({ type: 'info', icon: 'fa fa-play', message: 'Campaign started sending', timestamp: c.StartedAt });
      }
      if (c.SentAt && c.SentCount) {
        events.push({ type: 'info', icon: 'fa fa-paper-plane', message: `${c.SentCount} message${c.SentCount > 1 ? 's' : ''} sent`, timestamp: c.SentAt });
      }
      if (c.CompletedAt || (c.Status === 'sent' && c.SentAt)) {
        events.push({ type: 'success', icon: 'fa fa-check', message: 'Campaign completed successfully', timestamp: c.CompletedAt || c.SentAt });
      }
      if (c.Status === 'failed') {
        events.push({ type: 'danger', icon: 'fa fa-times', message: 'Campaign failed', timestamp: c.UpdatedAt || c.SentAt });
      }

      // Add click events from recipients (with channel breakdown for Both campaigns)
      const clickedCount = c.ClickedCount || 0;
      if (clickedCount > 0) {
        const clickedRecipients = this.recipients.filter(r => r.ClickedAt);
        const earliestClick = clickedRecipients.length > 0
          ? clickedRecipients.reduce((earliest, r) => !earliest || new Date(r.ClickedAt) < new Date(earliest) ? r.ClickedAt : earliest, null)
          : c.CompletedAt || c.SentAt;

        if (c.Type === 'Both') {
          // Split into SMS and Email click entries
          const smsClickRecipients = this.recipients.filter(r => r.SmsClickCount > 0);
          const emailClickRecipients = this.recipients.filter(r => r.EmailClickCount > 0);
          const totalSmsClicks = this.recipients.reduce((sum, r) => sum + (r.SmsClickCount || 0), 0);
          const totalEmailClicks = this.recipients.reduce((sum, r) => sum + (r.EmailClickCount || 0), 0);
          if (totalSmsClicks > 0) {
            events.push({ type: 'info', icon: 'fa fa-mouse-pointer', message: `${smsClickRecipients.length} recipient${smsClickRecipients.length > 1 ? 's' : ''} clicked an SMS link (${totalSmsClicks} total SMS clicks)`, timestamp: earliestClick });
          }
          if (totalEmailClicks > 0) {
            events.push({ type: 'info', icon: 'fa fa-mouse-pointer', message: `${emailClickRecipients.length} recipient${emailClickRecipients.length > 1 ? 's' : ''} clicked an Email link (${totalEmailClicks} total email clicks)`, timestamp: earliestClick });
          }
        } else {
          events.push({ type: 'info', icon: 'fa fa-mouse-pointer', message: `${clickedCount} recipient${clickedCount > 1 ? 's' : ''} clicked a link (${c.TotalClicks || clickedCount} total clicks)`, timestamp: earliestClick });
        }
      }

      // Sort newest first
      events.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      return events;
    }
  },

  created() {
    this.loadCampaignDetails();
  },

  beforeDestroy() {
    // Stop polling when leaving the page
    this.$store.dispatch('campaigns/stopCampaignPolling');
  },

  methods: {
    async loadClickData() {
      if (this.clicksLoaded) return;
      this.clicksLoading = true;
      try {
        const campaignId = this.$route.params.campaignId;
        const data = await this.$store.dispatch('campaigns/fetchCampaignClicks', campaignId);
        if (data) {
          this.clickData = data;
        }
        this.clicksLoaded = true;
      } catch (err) {
        console.error('Error loading click data:', err);
      } finally {
        this.clicksLoading = false;
      }
    },

    async loadCampaignDetails() {
      this.busy = true;
      try {
        const campaignId = this.$route.params.campaignId;
        await this.$store.dispatch('campaigns/fetchCampaignById', campaignId);

        // Get campaign from store
        const storeCampaign = this.$store.state.campaigns.currentCampaign;
        if (storeCampaign) {
          this.campaign = { ...storeCampaign };
        }

        // Also fetch recipients
        const storeRecipients = await this.$store.dispatch('campaigns/fetchCampaignRecipients', campaignId);
        if (storeRecipients && storeRecipients.length > 0) {
          const mapped = storeRecipients.map(r => ({
            ...r,
            Name: [r.FName, r.LName].filter(Boolean).join(' '),
            PatNum: r.PatientID
          }));
          // For Both campaigns, merge 2 rows per patient into 1 row showing both channels
          this.recipients = this.campaign.Type === 'Both' ? this.mergeRecipientsByPatient(mapped) : mapped;
        }

        // Start polling if campaign is currently sending
        if (this.campaign.Status === 'sending') {
          this.startPolling();
        }
      } catch (error) {
        console.error('Error loading campaign details:', error);
      } finally {
        this.busy = false;
      }
    },

    startPolling() {
      this.$store.dispatch('campaigns/startCampaignPolling', this.$route.params.campaignId);
      // Watch for store updates
      this.$watch(
        () => this.$store.state.campaigns.currentCampaign,
        (newVal) => {
          if (newVal) {
            this.campaign = { ...newVal };
          }
        },
        { deep: true }
      );
    },

    async retryFailed() {
      this.busy = true;
      try {
        await this.$store.dispatch('campaigns/retryCampaign', {
          campaignId: this.$route.params.campaignId,
          campaignType: this.campaign.Type || 'Email'
        });
        this.$toast?.success?.('Retrying failed recipients...');
        // Refresh and start polling
        await this.loadCampaignDetails();
      } catch (error) {
        console.error('Error retrying campaign:', error);
        this.$toast?.error?.(error.message || 'Failed to retry');
      } finally {
        this.busy = false;
      }
    },

    // Merge 2 recipient rows per patient into 1 row for Both (SMS+Email) campaigns
    mergeRecipientsByPatient(recipients) {
      const grouped = {};
      for (const r of recipients) {
        const key = r.PatientID;
        if (!grouped[key]) {
          grouped[key] = {
            PatientID: r.PatientID,
            PatNum: r.PatientID,
            Name: r.Name,
            FName: r.FName,
            LName: r.LName,
            Phone: null,
            Email: null,
            SmsStatus: null,
            EmailStatus: null,
            SmsClickedAt: null,
            EmailClickedAt: null,
            SmsClickCount: 0,
            EmailClickCount: 0,
            ClickCount: 0,
            ClickedAt: null
          };
        }
        const g = grouped[key];
        if (r.Channel === 'sms') {
          g.Phone = r.Phone;
          g.SmsStatus = r.Status;
          g.SmsClickedAt = r.ClickedAt;
          g.SmsClickCount = r.ClickCount || 0;
        } else if (r.Channel === 'email') {
          g.Email = r.Email;
          g.EmailStatus = r.Status;
          g.EmailClickedAt = r.ClickedAt;
          g.EmailClickCount = r.ClickCount || 0;
        }
        g.ClickCount = g.SmsClickCount + g.EmailClickCount;
        if (r.ClickedAt) {
          if (!g.ClickedAt || new Date(r.ClickedAt) < new Date(g.ClickedAt)) {
            g.ClickedAt = r.ClickedAt;
          }
        }
      }
      return Object.values(grouped);
    },

    goBack() {
      this.$router.push({ name: 'campaigns' });
    },

    viewPatient(recipient) {
      this.$router.push({ 
        name: 'patientprofile', 
        params: { patientID: `${this.siteID}-${recipient.PatNum}` } 
      });
    },

    getRecipientStatusVariant(status) {
      const s = (status || '').toLowerCase();
      const variants = {
        'delivered': 'success',
        'sent': 'info',
        'bounced': 'danger',
        'failed': 'danger',
        // Not a failure — the message was deliberately not sent (patient opted out,
        // messaging paused, WhatsApp window closed). Neutral grey, never red.
        'skipped': 'secondary',
        'pending': 'warning',
        'queued': 'warning',
        'opened': 'info',
        'clicked': 'primary'
      };
      return variants[s] || 'secondary';
    },

    // The reason a recipient was skipped or failed, as written by the backend on the
    // campaign_recipients row. Shown only for non-sent outcomes — a delivered recipient
    // may still carry a stale ErrorMessage from an earlier attempt.
    recipientStatusReason(item) {
      const s = ((item && item.Status) || '').toLowerCase();
      if (s !== 'skipped' && s !== 'failed' && s !== 'bounced' && s !== 'dropped') return '';
      return (item.ErrorMessage || '').trim();
    },

  },

  filters: {
    formatNumber(value) {
      if (!value) return '0';
      return value.toLocaleString();
    },
    // Turn snake_case / camelCase outcome keys into Title Case labels (e.g. no_answer -> No Answer)
    humanizeKey(value) {
      if (!value) return '';
      return String(value)
        .replace(/_/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\b\w/g, c => c.toUpperCase());
    }
  }
};
</script>

<style lang="scss" scoped>
// Channel badges for SMS+Email (Both) campaigns
.channel-badge {
  display: inline-block;
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  padding: 1px 5px;
  border-radius: 3px;
  margin-right: 4px;
  letter-spacing: 0.3px;
}
.channel-sms {
  background: rgba(58, 191, 148, 0.15);
  color: #2d9474;
}
.channel-email {
  background: rgba(84, 127, 255, 0.15);
  color: #3d5dff;
}

// Why a recipient wasn't reached (opt-out, bad number, ...). Sits under the status badge;
// clamped to two lines so one long carrier message can't stretch the table row — the full
// text stays available via the title attribute.
.recipient-status-reason {
  margin-top: 2px;
  font-size: 0.72rem;
  line-height: 1.25;
  color: #718096;
  max-width: 220px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.campaign-detail-page {
  // Back Navigation
  .back-nav {
    .back-btn {
      color: #718096;
      font-size: 0.9rem;
      text-decoration: none;
      
      &:hover {
        color: #3abf94;
      }
    }
  }

  // Sending Progress Card
  .sending-progress-card {
    border: none;
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    border-left: 4px solid #3abf94;
  }

  // Header Card
  .campaign-header-card {
    border: none;
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    
    .header-content {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 1.5rem;
      
      .header-left {
        flex: 1;
        min-width: 0;
        
        .title-row {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 0.5rem;
          margin-bottom: 0.75rem;
          
          .campaign-title {
            font-size: 1.5rem;
            font-weight: 700;
            color: #1a202c;
            margin: 0;
            line-height: 1.3;
          }
          
          .status-badge {
            font-size: 0.8rem;
            font-weight: 500;
            padding: 0.4rem 0.8rem;
            border-radius: 20px;
          }
        }
        
        .campaign-description {
          color: #4a5568;
          font-size: 0.95rem;
          line-height: 1.5;
          margin-bottom: 1rem;
        }
        
        .campaign-meta {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 0.5rem;
          
          .meta-item {
            display: flex;
            align-items: center;
            gap: 0.4rem;
            font-size: 0.85rem;
            color: #718096;
            
            .meta-icon {
              width: 28px;
              height: 28px;
              border-radius: 6px;
              background: linear-gradient(135deg, #3abf94, #2d9474);
              display: flex;
              align-items: center;
              justify-content: center;
              
              i {
                color: white;
                font-size: 0.8rem;
              }
            }
            
            .meta-text {
              font-weight: 600;
              color: #2d3748;
            }
          }
          
          .meta-divider {
            width: 4px;
            height: 4px;
            border-radius: 50%;
            background: #cbd5e0;
          }
        }
      }
      
      .header-right {
        display: flex;
        flex-shrink: 0;
        
        .action-btn {
          border-radius: 8px;
          font-weight: 500;
          padding: 0.5rem 1rem;
        }
      }
    }
  }

  // Metrics Grid
  .metrics-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 1rem;
    
    .metric-card {
      background: white;
      border-radius: 12px;
      padding: 1.25rem;
      display: flex;
      align-items: center;
      gap: 1rem;
      border: 1px solid #e2e8f0;
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
      transition: all 0.2s ease;
      
      &:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        transform: translateY(-2px);
      }
      
      .metric-icon-wrapper {
        width: 48px;
        height: 48px;
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        
        i {
          font-size: 1.25rem;
          color: white;
        }
        
        &.recipients { background: linear-gradient(135deg, #547fff, #3d5dff); }
        &.sent { background: linear-gradient(135deg, #3abf94, #2d9474); }
        &.delivered { background: linear-gradient(135deg, #10b981, #059669); }
        &.opened { background: linear-gradient(135deg, #06b6d4, #0891b2); }
        &.clicked { background: linear-gradient(135deg, #3b82f6, #2563eb); }
        &.bounced { background: linear-gradient(135deg, #ef4444, #dc2626); }
      }
      
      .metric-info {
        display: flex;
        flex-direction: column;
        min-width: 0;
        
        .metric-value {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1a202c;
          line-height: 1.2;
        }
        
        .metric-label {
          font-size: 0.75rem;
          color: #718096;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .metric-rate {
          font-size: 0.85rem;
          font-weight: 600;
          margin-top: 2px;
          
          &.success { color: #10b981; }
          &.info { color: #06b6d4; }
          &.primary { color: #3b82f6; }
          &.danger { color: #ef4444; }
        }
      }
    }
  }

  // Analytics Card
  .analytics-card {
    border: none;
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);

    ::v-deep .analytics-tabs {
      border-bottom: 2px solid #e2e8f0;
      margin: -1rem -1.25rem 0;
      padding: 0 1rem;

      .nav-link {
        padding: 1rem 1.25rem;
        font-weight: 500;
        color: #718096;
        border: none;
        border-bottom: 2px solid transparent;
        margin-bottom: -2px;
        transition: all 0.2s ease;
        
        &:hover {
          color: #3abf94;
          background: transparent;
        }
        
        &.active {
          color: #3abf94;
          border-bottom-color: #3abf94;
          background: transparent;
        }
      }
    }
  }

  // Chart Section
  .chart-section {
    padding: 1.5rem;
    background: #f8fafc;
    border-radius: 12px;

    .section-title {
      font-size: 1rem;
      font-weight: 600;
      color: #2d3748;
      margin-bottom: 1.25rem;
      
      i {
        color: #3abf94;
      }
    }
  }

  // Funnel Chart
  .funnel-chart {
    .funnel-stage {
      margin-bottom: 0.5rem;

      .funnel-bar {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0.75rem 1rem;
        border-radius: 8px;
        transition: width 0.5s ease;
        min-width: 120px;
        
        &.base {
          background: #e2e8f0;
          color: #4a5568;
        }
        
        &.sent { 
          background: linear-gradient(to right, #3abf94, #2d9474); 
          color: white; 
        }
        &.delivered { 
          background: linear-gradient(to right, #10b981, #059669); 
          color: white; 
        }
        &.opened { 
          background: linear-gradient(to right, #06b6d4, #0891b2); 
          color: white; 
        }
        &.clicked { 
          background: linear-gradient(to right, #3b82f6, #2563eb); 
          color: white; 
        }

        .funnel-label {
          font-weight: 500;
          font-size: 0.9rem;
        }

        .funnel-value {
          font-weight: 700;
          font-size: 0.9rem;
        }
      }
    }
  }

  // Donut Chart
  .donut-chart-container {
    height: 200px;
    display: flex;
    justify-content: center;
  }

  .chart-legend {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 1.5rem;
    margin-top: 1rem;

    .legend-item {
      display: flex;
      align-items: center;
      gap: 0.5rem;

      .legend-color {
        width: 10px;
        height: 10px;
        border-radius: 50%;

        &.delivered { background: #3abf94; }
        &.bounced { background: #ef4444; }
        &.pending { background: #f59e0b; }
      }

      .legend-label {
        color: #718096;
        font-size: 0.85rem;
      }

      .legend-value {
        font-weight: 600;
        color: #2d3748;
        font-size: 0.85rem;
      }
    }
  }

  .timeline-chart-container {
    height: 280px;
  }

  // Recipients Section
  .recipients-section {
    .recipients-toolbar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 1rem;
      margin-bottom: 1rem;
      flex-wrap: wrap;
      
      .search-input-wrapper {
        position: relative;
        flex: 1;
        min-width: 200px;
        max-width: 300px;
        
        .search-icon {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #a0aec0;
          pointer-events: none; // let clicks fall through to the input
        }

        // <b-form-input> renders a .form-control whose padding wins over a plain
        // scoped `.search-input` rule (the child-component root doesn't inherit the
        // scope attr), so the magnifying-glass icon overlapped the placeholder text.
        // ::v-deep pierces to the real input — same pattern as Drafts.vue's .btn-link.
        ::v-deep .search-input {
          padding-left: 38px;
          border-radius: 8px;
          border: 1px solid #e2e8f0;

          &:focus {
            border-color: #3abf94;
            box-shadow: 0 0 0 3px rgba(58, 191, 148, 0.1);
          }
        }
      }

      .filter-select {
        border-radius: 8px;
        min-width: 150px;
        border: 1px solid #e2e8f0;
        
        &:focus {
          border-color: #3abf94;
          box-shadow: 0 0 0 3px rgba(58, 191, 148, 0.1);
        }
      }
    }

    .recipients-table {
      .patient-cell {
        display: flex;
        flex-direction: column;
        gap: 2px;

        strong {
          color: #2d3748;
          font-size: 0.95rem;
        }

        span {
          font-size: 0.8rem;
        }
      }
      
      .view-btn {
        padding: 0.25rem 0.5rem;
      }
    }
    
    .recipients-footer {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: 1rem;
      padding-top: 1rem;
      border-top: 1px solid #e2e8f0;
      flex-wrap: wrap;
      gap: 1rem;
      
      .results-count {
        font-size: 0.85rem;
        color: #718096;
      }
    }
  }

  // Message Content Section
  .message-content-section {
    .message-block {
      .message-preview-card {
        background: #f8fafc;
        border: 1px solid #e2e8f0;
        border-radius: 12px;

        .sms-preview {
          .sms-bubble {
            background: #3abf94;
            color: white;
            padding: 1rem 1.25rem;
            border-radius: 18px 18px 4px 18px;
            max-width: 80%;
            line-height: 1.5;
            font-size: 0.95rem;
          }
        }

        .message-stats {
          display: flex;
          flex-wrap: wrap;
          gap: 1.5rem;
          
          .stat {
            font-size: 0.85rem;
            color: #718096;
          }
        }

        .email-preview {
          .email-subject {
            margin-bottom: 1rem;
            padding-bottom: 1rem;
            border-bottom: 1px solid #e2e8f0;
            
            label {
              font-weight: 600;
              color: #718096;
              margin-right: 0.5rem;
              font-size: 0.85rem;
            }
            
            span {
              color: #2d3748;
              font-weight: 500;
            }
          }

          .email-body {
            padding: 1rem;
            background: white;
            border-radius: 8px;
            border: 1px solid #e2e8f0;
            white-space: pre-wrap;
            line-height: 1.6;
            font-size: 0.95rem;
            color: #4a5568;
          }
        }
      }
    }
  }

  // Activity Log Section
  .activity-log-section {
    .activity-timeline {
      .activity-item {
        display: flex;
        align-items: flex-start;
        gap: 1rem;
        padding: 1rem 0;
        border-left: 2px solid #e2e8f0;
        margin-left: 14px;
        padding-left: 1.5rem;
        position: relative;

        &:last-child {
          border-left-color: transparent;
        }

        .activity-icon {
          position: absolute;
          left: -15px;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: white;
          border: 2px solid #e2e8f0;

          i {
            font-size: 0.75rem;
          }

          &.success {
            border-color: #3abf94;
            color: #3abf94;
          }
          &.info {
            border-color: #3b82f6;
            color: #3b82f6;
          }
          &.warning {
            border-color: #f59e0b;
            color: #f59e0b;
          }
          &.error {
            border-color: #ef4444;
            color: #ef4444;
          }
          &.default {
            border-color: #a0aec0;
            color: #a0aec0;
          }
        }

        .activity-content {
          flex: 1;

          .activity-message {
            margin-bottom: 0.25rem;
            color: #2d3748;
            font-size: 0.95rem;
          }

          .activity-time {
            font-size: 0.8rem;
            color: #a0aec0;
          }
        }
      }
    }
  }

  // Bot exclusion toggle
  .bot-exclusion-toggle {
    display: flex;
    align-items: center;

    .toggle-label {
      font-size: 0.85rem;
      color: #4a5568;
    }
  }

  // Additional metric card styles
  .metric-card {
    .metric-icon-wrapper {
      &.ctor { background: linear-gradient(135deg, #8b5cf6, #7c3aed); }
      &.unsubscribed { background: linear-gradient(135deg, #f59e0b, #d97706); }
      &.complaints { background: linear-gradient(135deg, #ef4444, #dc2626); }
    }

    .metric-info {
      .metric-subtitle {
        font-size: 0.7rem;
        color: #a0aec0;
        display: block;
      }

      .metric-rate {
        &.warning { color: #f59e0b; }
      }
    }
  }

  // Stats breakdown for Opens/Clicks tab
  .stats-breakdown {
    .stat-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.75rem 0;
      border-bottom: 1px solid #e2e8f0;

      &:last-child {
        border-bottom: none;
      }

      .stat-label {
        color: #718096;
        font-size: 0.9rem;
      }

      .stat-value {
        font-weight: 600;
        color: #2d3748;
        font-size: 1.1rem;
      }
    }
  }

  // Bounce stats
  .bounces-section {
    .bounce-stat-card {
      display: flex;
      align-items: center;
      gap: 1rem;
      padding: 1.25rem;
      background: white;
      border-radius: 12px;
      border: 1px solid #e2e8f0;
      margin-bottom: 1rem;

      .bounce-stat-icon {
        width: 48px;
        height: 48px;
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;

        i {
          font-size: 1.25rem;
          color: white;
        }
      }

      &.total .bounce-stat-icon {
        background: linear-gradient(135deg, #ef4444, #dc2626);
      }

      &.hard .bounce-stat-icon {
        background: linear-gradient(135deg, #991b1b, #7f1d1d);
      }

      &.soft .bounce-stat-icon {
        background: linear-gradient(135deg, #f59e0b, #d97706);
      }

      .bounce-stat-info {
        .bounce-stat-value {
          display: block;
          font-size: 1.5rem;
          font-weight: 700;
          color: #2d3748;
        }

        .bounce-stat-label {
          display: block;
          font-size: 0.85rem;
          color: #718096;
        }
      }
    }
  }

  // Summary cards for unsubscribed/complaints
  .unsubscribe-summary,
  .complaints-summary {
    .summary-card {
      display: flex;
      align-items: center;
      gap: 1.5rem;
      padding: 1.5rem;
      background: linear-gradient(135deg, rgba(245, 158, 11, 0.1), rgba(217, 119, 6, 0.1));
      border: 1px solid rgba(245, 158, 11, 0.2);
      border-radius: 12px;

      &.warning {
        background: linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(220, 38, 38, 0.1));
        border-color: rgba(239, 68, 68, 0.2);
      }

      .summary-icon {
        font-size: 2.5rem;
        color: #f59e0b;
      }

      &.warning .summary-icon {
        color: #ef4444;
      }

      .summary-content {
        .summary-value {
          display: block;
          font-size: 2rem;
          font-weight: 700;
          color: #2d3748;
        }

        .summary-label {
          display: block;
          font-size: 1rem;
          color: #4a5568;
          font-weight: 500;
        }

        .summary-rate {
          display: block;
          font-size: 0.85rem;
          color: #718096;
          margin-top: 0.25rem;
        }
      }
    }
  }

  // Email domains section
  .email-domains-section {
    .domains-chart {
      .domain-bar-row {
        display: flex;
        align-items: center;
        gap: 1rem;
        margin-bottom: 0.75rem;

        .domain-name {
          width: 120px;
          font-size: 0.9rem;
          color: #4a5568;
          font-weight: 500;
        }

        .domain-bar-container {
          flex: 1;
          height: 24px;
          background: #e2e8f0;
          border-radius: 4px;
          overflow: hidden;

          .domain-bar {
            height: 100%;
            background: linear-gradient(to right, #3abf94, #2d9474);
            border-radius: 4px;
            transition: width 0.5s ease;
          }
        }

        .domain-stats {
          width: 100px;
          text-align: right;

          .domain-count {
            font-weight: 600;
            color: #2d3748;
          }

          .domain-percentage {
            font-size: 0.8rem;
            color: #718096;
            margin-left: 0.25rem;
          }
        }
      }
    }
  }

  // Links table
  .links-table {
    .link-url {
      font-family: monospace;
      font-size: 0.85rem;
      color: #3b82f6;
      word-break: break-all;
    }
  }

  // Section subtitle
  .section-subtitle {
    font-size: 0.9rem;
  }
}

// Mobile Responsive Styles
@media (max-width: 991px) {
  .campaign-detail-page {
    .campaign-header-card {
      .header-content {
        flex-direction: column;
        
        .header-right {
          width: 100%;
          justify-content: flex-start;
          
          .action-btn {
            flex: 1;
          }
        }
      }
    }
    
    .metrics-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }
}

@media (max-width: 767px) {
  .campaign-detail-page {
    .campaign-header-card {
      .header-content {
        .header-left {
          .title-row {
            .campaign-title {
              font-size: 1.25rem;
            }
          }
          
          .campaign-meta {
            .meta-divider {
              display: none;
            }
            
            .meta-item {
              flex-basis: 100%;
            }
          }
        }
      }
    }
    
    .metrics-grid {
      grid-template-columns: 1fr;
      
      .metric-card {
        .metric-info {
          .metric-value {
            font-size: 1.25rem;
          }
        }
      }
    }
    
    .analytics-card {
      ::v-deep .analytics-tabs {
        .nav-link {
          padding: 0.75rem 0.5rem;
          font-size: 0.85rem;
        }
      }
    }
    
    .chart-section {
      padding: 1rem;
    }
    
    .chart-legend {
      flex-direction: column;
      align-items: flex-start;
      gap: 0.75rem;
    }
    
    .recipients-section {
      .recipients-toolbar {
        flex-direction: column;
        align-items: stretch;
        
        .search-input-wrapper {
          max-width: none;
        }
        
        .filter-select {
          width: 100%;
        }
      }
      
      .recipients-footer {
        flex-direction: column;
        align-items: flex-start;
      }
    }
    
    .message-content-section {
      .message-preview-card {
        .sms-preview {
          .sms-bubble {
            max-width: 100%;
          }
        }
      }
    }
  }
}

@media (max-width: 480px) {
  .campaign-detail-page {
    .campaign-header-card {
      .header-content {
        .header-right {
          flex-direction: column;
          
          .action-btn {
            width: 100%;
            margin-right: 0 !important;
            margin-bottom: 0.5rem;
            
            &:last-child {
              margin-bottom: 0;
            }
          }
        }
      }
    }
  }

  // Click tracking tab
  .clicks-section {
    .click-timeline {
      .click-event-item {
        display: flex;
        align-items: flex-start;
        padding: 10px 0;
        border-bottom: 1px solid #f1f5f9;

        &:last-child { border-bottom: none; }

        .click-event-icon {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: rgba(59, 130, 246, 0.1);
          color: #3b82f6;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 12px;
          flex-shrink: 0;
          font-size: 0.8rem;
        }

        .click-event-content {
          flex: 1;
          p { font-size: 0.9rem; color: #334155; }
          small { font-size: 0.8rem; }
        }
      }
    }
  }
}
</style>
